import { useEffect, useMemo, useState } from 'react'
import { Field, NativeSelect, Skeleton, Stack, Text } from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'
import { EmailTemplateEditor } from './EmailTemplateEditor'
import { toaster } from './ui/toaster'
import { useTranslation } from '../i18n'
import {
  DEFAULT_TEMPLATE_LANGUAGE, TEMPLATE_LANGUAGES, fetchEventTemplates,
  fetchGlobalTemplates, fetchTemplateDefaults, resolveContent, saveEventTemplate,
  sendTestEmail, setEventEmailLanguage,
} from '../services/emailTemplates'

const LANGUAGE_LABELS = { es: 'Español', en: 'English' }

/**
 * Per-event email templates. Event admins own the prize mail their own
 * participants receive, and the reel share caption (plain text, not an email);
 * the invite template is sysadmin copy, so an event admin does not see it at
 * all.
 */
export function EventEmailTemplates({ eventId, emailLanguage }) {
  const { isSysAdmin } = useAuth()
  const { t, lang: uiLang } = useTranslation()
  const [defaults, setDefaults] = useState(null)
  const [stored, setStored] = useState(null)
  const [globals, setGlobals] = useState({})
  const [error, setError] = useState(null)
  // Derived, not synced: the saved value is the source of truth, and `pending`
  // only holds the optimistic choice until the event doc catches up.
  const [pending, setPending] = useState(null)
  const language = pending || emailLanguage || DEFAULT_TEMPLATE_LANGUAGE

  useEffect(() => {
    if (!eventId) return undefined
    let cancelled = false
    // The global defaults are sysadmin-only; an event admin simply falls back
    // to the bundled copy, which is what the sender would use anyway.
    Promise.all([
      fetchTemplateDefaults(eventId),
      fetchEventTemplates(eventId),
      isSysAdmin ? fetchGlobalTemplates() : Promise.resolve({}),
    ])
      .then(([bundled, eventDocs, globalDocs]) => {
        if (cancelled) return
        setDefaults(bundled)
        setStored(eventDocs)
        setGlobals(globalDocs)
      })
      .catch((err) => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [eventId, isSysAdmin])

  const handleLanguage = async (next) => {
    setPending(next)
    try {
      await setEventEmailLanguage(eventId, next)
    } catch (err) {
      setPending(null)
      toaster.create({ title: t('templates.saveFailed'), description: err.message, type: 'error' })
    }
  }

  // Memoized: the editors re-seed their draft when this identity changes, so a
  // fresh object every render — switching the event's language, say — would
  // discard whatever the admin is typing.
  const editors = useMemo(() => {
    if (!defaults || !stored) return []
    return Object.entries(defaults.specs)
      .filter(([, spec]) => spec.scope === 'event')
      .map(([templateId, spec]) => ({
        templateId,
        spec,
        defaultContent: globals[templateId] || defaults.templates[templateId],
        samples: defaults.samples?.[templateId],
        content: resolveContent({
          stored: stored[templateId],
          globalContent: globals[templateId],
          bundled: defaults.templates[templateId],
          spec,
        }),
      }))
  }, [defaults, stored, globals])

  if (!eventId) return null

  const loading = !error && (!defaults || !stored)

  return (
    <>
      <Stack gap={4}>
        <Text fontSize="sm" color="var(--mc-text-muted)">{t('templates.eventSubtitle')}</Text>

        <Field.Root maxW="240px">
          <Field.Label>{t('templates.language')}</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={language}
              onChange={(e) => handleLanguage(e.target.value)}
              bg="var(--mc-input-bg)"
            >
              {TEMPLATE_LANGUAGES.map((code) => (
                <option key={code} value={code}>{LANGUAGE_LABELS[code] || code}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Field.HelperText>{t('templates.languageHelp')}</Field.HelperText>
        </Field.Root>

        {error && <Text color="red.300">{error}</Text>}
        {loading ? (
          <Skeleton height="200px" borderRadius="md" />
        ) : (
          editors.map(({ templateId, spec, samples, content, defaultContent }) => (
            <EmailTemplateEditor
              key={templateId}
              templateId={templateId}
              spec={spec}
              samples={samples}
              initialLanguage={language || uiLang}
              defaultContent={defaultContent}
              content={content}
              onSave={async (next) => {
                await saveEventTemplate(eventId, templateId, next)
                setStored((prev) => ({ ...prev, [templateId]: next }))
              }}
              onSendTest={(args) => sendTestEmail({ templateId, eventId, ...args })}
            />
          ))
        )}
      </Stack>
    </>
  )
}
